import { motion } from 'framer-motion';
import React, { LegacyRef, useEffect, useMemo, useState } from 'react';
import ReactCountryFlag from 'react-country-flag';
import { FaChevronDown } from 'react-icons/fa6';
import useCountryActions from '../../../hooks/APIHooks/users/useCountryActions';
import useUserClickObserver from '../../../hooks/useUserClickObserver';
import { SelectData } from '../../../types/common';
import { Country } from '../../../types/countries';

interface IAcademiaCountrySelect {
  name: string;
  label: string;
  setCountry: React.Dispatch<React.SetStateAction<SelectData>>;
  defaultCountryCode?: string;
  isRequired?: boolean;
  isDisabled?: boolean;
}

const AcademiaCountrySelect = ({
  name,
  label,
  setCountry,
  defaultCountryCode,
  isRequired,
  isDisabled,
}: IAcademiaCountrySelect) => {
  // integration of react hooks here
  const [searchText, setSearchText] = useState<string>('');
  const [showCountries, setShowCountries] = useState<boolean>(false);
  const [selectedCountry, setSelectedCountry] = useState<Country | null>(null);

  // integration of custom API hooks here
  const { getCountryListHandler } = useCountryActions('countryList');

  // integration of custom hooks here
  const selectRef = useUserClickObserver(setShowCountries);

  // setting default value of country here
  useEffect(() => {
    if (getCountryListHandler.data?.is_success) {
      if (!defaultCountryCode) {
        setSelectedCountry(getCountryListHandler.data.data[0]);
        setCountry({
          id: getCountryListHandler.data.data[0].id,
          name: getCountryListHandler.data.data[0].country_name,
          code: getCountryListHandler.data.data[0].iso_code,
        });
      } else {
        const defaultCountry = getCountryListHandler.data.data.find(
          (country) => country.iso_code === defaultCountryCode
        ) as Country;

        setSelectedCountry(defaultCountry);
        setCountry({
          id: defaultCountry.id,
          name: defaultCountry.country_name,
          code: defaultCountry.iso_code,
        });
      }
    }
  }, [defaultCountryCode, setCountry, getCountryListHandler.data]);

  // setting countries based on search result declared here
  const countryOptions = useMemo(() => {
    if (!getCountryListHandler.data?.is_success) return [];

    return getCountryListHandler.data.data.filter((country) =>
      searchText
        ? country.country_name.toLowerCase().includes(searchText.toLowerCase())
        : country
    );
  }, [searchText, getCountryListHandler.data]);

  // handler function to select country from the list declared here
  const selectCountryHandler = (country: Country) => {
    setSelectedCountry(country);
    setCountry({
      id: country.id,
      name: country.country_name,
      code: country.iso_code,
    });

    setSearchText('');
    setShowCountries(false);
  };

  // rendering academia country select component here
  return (
    <div>
      <label
        htmlFor={`input-${name}`}
        title={isRequired ? 'This field is required' : ''}
        className='block mb-1 text-base-dark-blue font-medium text-base'
      >
        {label} {isRequired && <span className='text-base-red'>*</span>}
      </label>

      <div
        tabIndex={isDisabled ? -1 : 0}
        ref={selectRef as LegacyRef<HTMLDivElement>}
        className='relative w-full focus:ring-0 focus:outline-none'
        onFocus={() => !isDisabled && setShowCountries(true)}
        onBlur={() => setShowCountries(false)}
      >
        <div
          title={selectedCountry?.country_name}
          // onClick={() => setShowCountries((state) => !state)}
          className={`relative peer/select border-2 rounded-md p-3 text-base-dark-blue font-semibold duration-300 text-base border-base-dark-blue w-full md:min-w-[160px] ${
            isDisabled
              ? 'opacity-70 cursor-not-allowed'
              : 'active:scale-95 cursor-pointer'
          }`}
        >
          <div className='flex items-center gap-2'>
            <ReactCountryFlag
              style={{ width: '2em', height: '1.42em' }}
              countryCode={selectedCountry?.iso_code as string}
              svg
            />
            <p className='font-semibold'>{`${selectedCountry?.country_name}`}</p>
          </div>

          <div
            className={`absolute right-3 top-1/2 -translate-y-1/2 duration-300 bg-base-white ${
              showCountries ? 'rotate-180' : 'rotate-0'
            }`}
          >
            <FaChevronDown className='h-6 w-6' />
          </div>
        </div>

        <motion.div
          initial={{ height: 0 }}
          animate={showCountries ? { height: 'fit-content' } : { height: 0 }}
          className='absolute left-0 top-full bg-base-white shadow-academia-sm w-full z-20 mt-2 rounded-md text-base-dark-blue overflow-hidden text-base'
        >
          <div className='m-3'>
            <input
              tabIndex={-1}
              type='text'
              className='border-2 focus:ring-4 focus:ring-base-dark-blue rounded-md px-3 py-2 w-full text-base-dark-blue font-semibold duration-300 text-base border-base-dark-blue active:scale-[0.98]'
              placeholder='Search countries...'
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
          </div>

          <motion.ul
            initial={{ height: 0 }}
            animate={
              showCountries
                ? countryOptions.length > 5
                  ? { height: 220 }
                  : { height: 'fit-content' }
                : { height: 0 }
            }
            className='academia-scrollbar overflow-y-auto'
          >
            {countryOptions.map((country, index) => (
              <motion.li
                initial={{ opacity: 0 }}
                title={country.country_name}
                animate={showCountries ? { opacity: 1 } : { opacity: 0 }}
                key={index}
                onClick={() => {
                  selectCountryHandler(country);
                  selectRef.current?.blur();
                }}
                className='py-2 px-3 hover:bg-gray-200 duration-300 cursor-pointer [&:not(:last-child)]:border-b border-gray-300'
              >
                <div className='flex items-center gap-3'>
                  <ReactCountryFlag
                    style={{ width: '2em', height: '2em' }}
                    countryCode={country.iso_code}
                    svg
                  />
                  <p className='font-semibold'>{`${country.country_name}`}</p>
                </div>
              </motion.li>
            ))}
          </motion.ul>
        </motion.div>
      </div>
    </div>
  );
};

export default AcademiaCountrySelect;
